import { AnswerStep } from '../../../../../dorian-shared/types/branch/AnswerStep';
import { BranchStep } from '../../../../../dorian-shared/types/branch/BranchStep';
import { HubOption, Mod } from '../../../../../dorian-shared/types/branch/HubOption';
import { ReplayDefaults } from '../../../../../dorian-shared/types/branch/HubOptionReplay.types';
import { MemoryDTO, StepCheckSwitch } from '../../../Book/MemoryBank/memoryBankTypes';
import {
  buildBranchTitle, BuildBranchTitleContext, pickNextFreeTitle, rootOfBranchTitle,
} from './buildBranchTitle';

/** Form-serialized payload shape (after JSON.parse(JSON.stringify(...))), before `api.put`. */
export type ArrPayload = {
  title?: string,
  /** -1 (number or string) signals "create a new branch via plain goto". */
  gotoBranchId?: number | string | null,
  steps?: Array<Partial<BranchStep>>,
};

/** Optional hint the frontend attaches; backend strips before persistence. */
type WithBranchTitleHint = { branchTitle?: string };

// Form-serialized fields may arrive as `-1` (number) or `'-1'` (string).
function isCreateNewBranchSignal(gotoBranchId: unknown): boolean {
  const CREATE_NEW_BRANCH_SIGNAL = -1;
  if (gotoBranchId === null || gotoBranchId === undefined || gotoBranchId === '') return false;
  return Number(gotoBranchId) === CREATE_NEW_BRANCH_SIGNAL;
}

// Mutates `taken` so subsequent siblings in the same payload don't collide.
function reserveTitle(root: string, taken: Set<string>): string {
  const final = pickNextFreeTitle(root, taken);
  if (final) taken.add(final);
  return final;
}

function assignTitle<T extends WithBranchTitleHint>(
  target: T,
  context: BuildBranchTitleContext,
  memories: MemoryDTO[],
  taken: Set<string>,
  suffix = '',
): void {
  const base = buildBranchTitle(context, memories);
  const root = appendSuffix(base, suffix);
  const title = reserveTitle(root, taken);
  if (title) {
    // eslint-disable-next-line no-param-reassign
    target.branchTitle = title;
  }
}

function enrichAnswers(
  answers: Array<Partial<AnswerStep> & WithBranchTitleHint> | undefined,
  memories: MemoryDTO[],
  taken: Set<string>,
): void {
  if (!Array.isArray(answers)) return;
  answers.forEach((answer) => {
    if (!isCreateNewBranchSignal(answer.gotoBranchId)) return;
    assignTitle(answer, { kind: 'answer', text: answer.text ?? '' }, memories, taken);
  });
}

function enrichSwitch(step: Partial<BranchStep>, memories: MemoryDTO[], taken: Set<string>): void {
  if (!Array.isArray(step.switch) || !step.check) return;
  const { check } = step;
  step.switch.forEach((outcome: StepCheckSwitch & WithBranchTitleHint) => {
    if (!isCreateNewBranchSignal(outcome.gotoBranchId)) return;
    assignTitle(outcome, { kind: 'check', check, outcome }, memories, taken);
  });
}

// title / replay.text mods are linked to their goto mod by sortOrder.
function collectOverrideTextsBySortOrder(mods: Mod[]): {
  titles: Map<number, string>,
  replayTexts: Map<number, string>,
} {
  const titles = new Map<number, string>();
  const replayTexts = new Map<number, string>();
  mods.forEach((mod) => {
    const val = mod?.val as { text?: string } | undefined;
    const text = typeof val?.text === 'string' ? val.text.trim() : '';
    if (!text) return;
    if (mod.prop === 'title') titles.set(mod.sortOrder, text);
    else if (mod.prop === 'replay.text') replayTexts.set(mod.sortOrder, text);
  });
  return { titles, replayTexts };
}

// Suffix that keeps two overrides with the same source text but different
// conditions from collapsing into the same branch name.
function whenVariableSuffix(mod: Mod, memories: MemoryDTO[]): string {
  const rules = (mod?.when as { rules?: Array<{ variableId?: number | string }> } | undefined)?.rules;
  if (!Array.isArray(rules) || rules.length === 0) return '';
  const names: string[] = [];
  rules.forEach((rule) => {
    const id = rule?.variableId;
    if (id === undefined || id === null || id === '') return;
    const numeric = Number(id);
    if (Number.isNaN(numeric)) return;
    const mem = memories.find((m) => Number(m.id) === numeric);
    const name = mem?.name?.replace(/[^A-Za-z0-9]/g, '');
    if (name) names.push(name);
  });
  return names.join('_');
}

function appendSuffix(root: string, suffix: string): string {
  if (!root) return root;
  if (!suffix) return root;
  return `${root}_${suffix}`;
}

function enrichHubOption(
  option: Partial<HubOption> & WithBranchTitleHint,
  memories: MemoryDTO[],
  taken: Set<string>,
): void {
  if (isCreateNewBranchSignal(option.gotoBranchId)) {
    assignTitle(option, { kind: 'hubOption', title: option.title ?? '' }, memories, taken);
  }

  const replay = option.replay as (ReplayDefaults & WithBranchTitleHint & { gotoBranchId?: number }) | null | undefined;
  if (replay && isCreateNewBranchSignal(replay.gotoBranchId)) {
    assignTitle(replay, { kind: 'hubReplay', optionTitle: option.title ?? '' }, memories, taken);
  }

  const mods = option.mods as Mod[] | undefined;
  if (!Array.isArray(mods)) return;
  const optionTitle = option.title ?? '';
  const { titles: overrideTitles, replayTexts: overrideReplayTexts } = collectOverrideTextsBySortOrder(mods);

  mods.forEach((mod) => {
    const val = mod.val as (Record<string, unknown> & WithBranchTitleHint & { gotoBranchId?: number }) | undefined;
    if (!val || !isCreateNewBranchSignal(val.gotoBranchId)) return;

    const condSuffix = whenVariableSuffix(mod, memories);

    if (mod.prop === 'goto') {
      const pairedText = overrideTitles.get(mod.sortOrder);
      const ctx: BuildBranchTitleContext = pairedText
        ? { kind: 'hubOverride', optionTitle: pairedText }
        : { kind: 'hubOverride', optionTitle };
      assignTitle(val, ctx, memories, taken, condSuffix);
      return;
    }

    // Author-supplied replay text IS the replay name — no Replay_ prefix on top.
    if (mod.prop === 'replay.goto') {
      const pairedText = overrideReplayTexts.get(mod.sortOrder);
      const ctx: BuildBranchTitleContext = pairedText
        ? { kind: 'hubOverride', optionTitle: pairedText }
        : { kind: 'hubReplay', optionTitle };
      assignTitle(val, ctx, memories, taken, condSuffix);
      return;
    }

    assignTitle(val, { kind: 'hubOverride', optionTitle }, memories, taken, condSuffix);
  });
}

function enrichTopLevelPlainGoto<T extends ArrPayload & WithBranchTitleHint>(arr: T, taken: Set<string>): void {
  if (!isCreateNewBranchSignal(arr.gotoBranchId)) return;
  if (!arr.title) return;
  const root = rootOfBranchTitle(arr.title);
  const title = reserveTitle(root, taken);
  if (!title) return;
  // eslint-disable-next-line no-param-reassign
  arr.branchTitle = title;
}

// Callers must substitute the edited branch's persisted title with the current
// form value in `existingTitles` — an unsaved rename still occupies that slot.
export function enrichArrWithBranchTitles<T extends ArrPayload>(
  arr: T,
  memories: MemoryDTO[],
  existingTitles: string[] = [],
): T {
  if (!arr) return arr;
  const taken = new Set<string>(existingTitles.filter(Boolean));
  enrichTopLevelPlainGoto(arr as T & WithBranchTitleHint, taken);
  if (!Array.isArray(arr.steps)) return arr;
  arr.steps.forEach((step) => {
    enrichAnswers(step.answers, memories, taken);
    enrichSwitch(step, memories, taken);
    if (Array.isArray(step.hubOptions)) {
      step.hubOptions.forEach((option) => enrichHubOption(option, memories, taken));
    }
  });
  return arr;
}
